import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CustomerProvider } from './context/CustomerContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute, CompanyAdminRoute, AdminRoute, PlatformAdminRoute, ProfessionalAdminRoute, CrmRoute, ConsultingRoute } from './components/ProtectedRoute';
import type { UserRole } from './types/auth';
import InactivityTimeout from './components/InactivityTimeout';
import ResumeSessionModal from './components/ResumeSessionModal';
import { BreadcrumbProvider } from './context/BreadcrumbContext';

// ─── Lazy-loaded pages ──────────────────────────────────────────
// Public pages
const Landing = React.lazy(() => import('./pages/Landing'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Features = React.lazy(() => import('./pages/Features'));
const InvestorRelations = React.lazy(() => import('./pages/InvestorRelations'));
const Construction = React.lazy(() => import('./pages/Construction'));
const Legal = React.lazy(() => import('./pages/Legal'));
const Careers = React.lazy(() => import('./pages/Careers'));
const About = React.lazy(() => import('./pages/About'));
const Blog = React.lazy(() => import('./pages/Blog'));
const UserGuide = React.lazy(() => import('./pages/UserGuide'));
const TrialExpired = React.lazy(() => import('./pages/TrialExpired'));
const SubscriptionSuccess = React.lazy(() => import('./pages/SubscriptionSuccess'));
const EmailVerificationRequired = React.lazy(() => import('./pages/EmailVerificationRequired'));
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'));

// Dashboards
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CompanyAdminDashboardV2 = React.lazy(() => import('./pages/CompanyAdminDashboardV2'));
const PMDashboardV2 = React.lazy(() => import('./pages/PMDashboardV2'));
const PlatformAdminDashboardV2 = React.lazy(() => import('./pages/PlatformAdminDashboardV2'));

// Projects
const Projects = React.lazy(() => import('./pages/Projects'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const ProjectMetrics = React.lazy(() => import('./pages/ProjectMetrics').then(m => ({ default: m.ProjectMetrics })));
const NewProjectWizard = React.lazy(() => import('./pages/NewProjectWizard').then(m => ({ default: m.NewProjectWizard })));
const MeetingNotes = React.lazy(() => import('./pages/MeetingNotes'));
const MeetingNotesHub = React.lazy(() => import('./pages/MeetingNotesHub'));
const ProjectWhiteboard = React.lazy(() => import('./pages/ProjectWhiteboard'));
const ProjectRegisters = React.lazy(() => import('./pages/ProjectRegisters'));
const ProjectFieldLogs = React.lazy(() => import('./pages/ProjectFieldLogs'));
const ProjectProgressBilling = React.lazy(() => import('./pages/ProjectProgressBilling'));
const ProjectDocuments = React.lazy(() => import('./pages/ProjectDocuments'));
const ApprovalWorkflows = React.lazy(() => import('./pages/ApprovalWorkflows'));
const PhaseTemplates = React.lazy(() => import('./pages/PhaseTemplates'));
const Programs = React.lazy(() => import('./pages/Programs'));
const CrmHome = React.lazy(() => import('./features/crm/pages/CrmHome'));
const ConsultingHome = React.lazy(() => import('./features/consulting/pages/ConsultingHome'));
const ConsultingClients = React.lazy(() => import('./features/consulting/pages/ConsultingClients'));
const ConsultingClientDetail = React.lazy(() => import('./features/consulting/pages/ConsultingClientDetail'));
const ConsultingRfps = React.lazy(() => import('./features/consulting/pages/ConsultingRfps'));
const CrmAccountDetail = React.lazy(() => import('./features/crm/pages/AccountDetail'));
const CrmCustomerDetail = React.lazy(() => import('./features/crm/pages/CrmCustomerDetail'));
const CrmOpportunityDetail = React.lazy(() => import('./features/crm/pages/OpportunityDetail'));
const CrmSettings = React.lazy(() => import('./features/crm/pages/CrmSettings'));
const CrmPipelinePage = React.lazy(() => import('./features/crm/pages/PipelinePage'));
const CrmAccountsPage = React.lazy(() => import('./features/crm/pages/AccountsPage'));
const CrmLeadsPage = React.lazy(() => import('./features/crm/pages/LeadsPage'));
const CrmContactsPage = React.lazy(() => import('./features/crm/pages/ContactsPage'));
const CrmContactDetail = React.lazy(() => import('./features/crm/pages/ContactDetail'));
const CrmLeadDetail = React.lazy(() => import('./features/crm/pages/LeadDetail'));
const CrmReportsPage = React.lazy(() => import('./features/crm/pages/ReportsPage'));
const CrmActivitiesPage = React.lazy(() => import('./features/crm/pages/ActivitiesPage'));
const Timeline2 = React.lazy(() => import('./pages/Timeline2'));
const ProjectApprovals = React.lazy(() => import('./pages/ProjectApprovals'));
const AIInsightsDashboard = React.lazy(() => import('./pages/AIInsightsDashboard'));
const AIGeneratedRequirements = React.lazy(() => import('./pages/AIGeneratedRequirements').then(m => ({ default: m.AIGeneratedRequirements })));
const StrategicProjects = React.lazy(() => import('./pages/StrategicProjects'));
const ProjectArchive = React.lazy(() => import('./pages/ProjectArchive'));
const Customers = React.lazy(() => import('./pages/Customers'));
const CustomerDetail = React.lazy(() => import('./pages/CustomerDetail'));

// Requirements
const Requirements = React.lazy(() => import('./pages/Requirements'));
const RequirementMetrics = React.lazy(() => import('./pages/RequirementMetrics').then(m => ({ default: m.RequirementMetrics })));

// Taskboard
const TaskBoard = React.lazy(() => import('./pages/TaskBoard'));
const TaskBoardBacklog = React.lazy(() => import('./pages/TaskBoardBacklog'));
const TaskBoardRequirementDetail = React.lazy(() => import('./pages/TaskBoardRequirementDetail'));
const SprintArchive = React.lazy(() => import('./pages/SprintArchive'));

// Reports
const SprintReport = React.lazy(() => import('./pages/SprintReport'));
const VelocityReport = React.lazy(() => import('./pages/VelocityReport'));
const LeadershipReport = React.lazy(() => import('./pages/LeadershipReport'));
const ProgramGantt = React.lazy(() => import('./pages/ProgramGantt'));
const RequirementAgingReport = React.lazy(() => import('./pages/RequirementAgingReport'));
const TimeTrackingReport = React.lazy(() => import('./pages/TimeTrackingReport'));

// Wiki & Test Center (heavy: tldraw, React Flow, TipTap)
const Wiki = React.lazy(() => import('./pages/Wiki'));
const TestCenter = React.lazy(() => import('./pages/TestCenter'));
const TestRunDetail = React.lazy(() => import('./pages/TestRunDetail'));

// Admin
const CompanySettings = React.lazy(() => import('./pages/CompanySettings'));
const CompanyBilling = React.lazy(() => import('./pages/CompanyBilling'));
const CompanyManagement = React.lazy(() => import('./pages/CompanyManagement'));
const CompanyDetail = React.lazy(() => import('./pages/CompanyDetail'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const TeamManagement = React.lazy(() => import('./pages/TeamManagement'));
const TechStackManagement = React.lazy(() => import('./pages/TechStackManagement'));
const AccountSettings = React.lazy(() => import('./pages/AccountSettings'));
const SystemHealth = React.lazy(() => import('./pages/SystemHealth').then(m => ({ default: m.SystemHealth })));
const AIConfigurationPage = React.lazy(() => import('./pages/AIConfiguration'));
const JiraConfiguration = React.lazy(() => import('./pages/JiraConfiguration'));
const JiraWebhookLogs = React.lazy(() => import('./pages/JiraWebhookLogs'));
const JiraIntegration = React.lazy(() => import('./pages/JiraIntegration').then(m => ({ default: m.JiraIntegration })));
const CompanySetupWizard = React.lazy(() => import('./pages/CompanySetupWizard'));
const UserLocationMap = React.lazy(() => import('./pages/UserLocationMap'));
const BlogManagement = React.lazy(() => import('./pages/BlogManagement'));
const AIUsage = React.lazy(() => import('./pages/AIUsage'));
const ReleaseNotes = React.lazy(() => import('./pages/ReleaseNotes'));
const Retrospectives = React.lazy(() => import('./pages/Retrospectives'));

// Finances
const FinancesDashboard = React.lazy(() => import('./pages/FinancesDashboard'));
const PricingPlanManagement = React.lazy(() => import('./pages/PricingPlanManagement'));
const CompanySubscriptionManagement = React.lazy(() => import('./pages/CompanySubscriptionManagement'));
const StripePayments = React.lazy(() => import('./pages/StripePayments'));
const ProfessionalServices = React.lazy(() => import('./pages/ProfessionalServices'));
const MySkills = React.lazy(() => import('./pages/MySkills'));
const TeamSkills = React.lazy(() => import('./pages/TeamSkills'));
const SkillBank = React.lazy(() => import('./pages/SkillBank'));
const SkillsMatrix = React.lazy(() => import('./pages/SkillsMatrix'));
const MyCertifications = React.lazy(() => import('./pages/MyCertifications'));
const CertificationsDashboard = React.lazy(() => import('./pages/CertificationsDashboard'));
const MyTime = React.lazy(() => import('./pages/MyTime'));
const BillingReports = React.lazy(() => import('./pages/BillingReports'));
const BillingRates = React.lazy(() => import('./pages/BillingRates'));
const StandardRateCard = React.lazy(() => import('./pages/StandardRateCard'));
const TimesheetApprovals = React.lazy(() => import('./pages/TimesheetApprovals'));
const Invoices = React.lazy(() => import('./pages/Invoices'));
const InvoiceDetail = React.lazy(() => import('./pages/InvoiceDetail'));
const Resourcing = React.lazy(() => import('./pages/Resourcing'));

// ─── Loading fallback ───────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background-primary">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────

// Roles that can access general collaboration pages (project detail, requirements,
// meeting notes, etc.). Excludes stakeholder (view-only assigned-projects flow)
// and executive (read-only reporting only). Customer-role is a collaborator
// scoped to their assigned Customer's projects — same level of project access
// as contributor, just with a different tenancy boundary enforced server-side.
const nonStakeholderRoles: UserRole[] = ['platform_admin', 'company_admin', 'professional_admin', 'project_manager', 'project_manager_lite', 'contributor', 'taskboard_user', 'customer'];

function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <AuthProvider>
        <InactivityTimeout />
        <ResumeSessionModal />
        <ThemeProvider>
          <CustomerProvider>
          <BreadcrumbProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/investor-relations" element={<InvestorRelations />} />
          <Route path="/construction" element={<Construction />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="/trial-expired" element={<TrialExpired />} />
          <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route path="/email-verification-required" element={<EmailVerificationRequired />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/settings"
            element={
              <CompanyAdminRoute>
                <CompanySettings />
              </CompanyAdminRoute>
            }
          />

          <Route
            path="/company/billing"
            element={
              <CompanyAdminRoute>
                <CompanyBilling />
              </CompanyAdminRoute>
            }
          />

          <Route
            path="/company/jira"
            element={
              <CompanyAdminRoute>
                <JiraConfiguration />
              </CompanyAdminRoute>
            }
          />

          <Route
            path="/admin/professional-services"
            element={
              <CompanyAdminRoute>
                <ProfessionalServices />
              </CompanyAdminRoute>
            }
          />
          {/* Old path → new Professional Services hub */}
          <Route path="/admin/consulting" element={<Navigate to="/admin/professional-services" replace />} />
          <Route
            path="/admin/approval-workflows"
            element={
              <CompanyAdminRoute>
                <ApprovalWorkflows />
              </CompanyAdminRoute>
            }
          />
          <Route
            path="/admin/phase-templates"
            element={
              <CompanyAdminRoute>
                <PhaseTemplates />
              </CompanyAdminRoute>
            }
          />
          <Route
            path="/admin/programs"
            element={
              <CompanyAdminRoute>
                <Programs />
              </CompanyAdminRoute>
            }
          />
          <Route
            path="/pro"
            element={
              <CrmRoute>
                <CrmHome />
              </CrmRoute>
            }
          />
          <Route path="/pro/pipeline" element={<CrmRoute><CrmPipelinePage /></CrmRoute>} />
          <Route path="/pro/accounts" element={<CrmRoute><CrmAccountsPage /></CrmRoute>} />
          <Route path="/pro/leads" element={<CrmRoute><CrmLeadsPage /></CrmRoute>} />
          <Route path="/pro/leads/:id" element={<CrmRoute><CrmLeadDetail /></CrmRoute>} />
          <Route path="/pro/contacts" element={<CrmRoute><CrmContactsPage /></CrmRoute>} />
          <Route path="/pro/contacts/:id" element={<CrmRoute><CrmContactDetail /></CrmRoute>} />
          <Route path="/pro/reports" element={<CrmRoute><CrmReportsPage /></CrmRoute>} />
          <Route path="/pro/activities" element={<CrmRoute><CrmActivitiesPage /></CrmRoute>} />
          <Route path="/pro/accounts/:id" element={<CrmRoute><CrmAccountDetail /></CrmRoute>} />
          <Route path="/pro/customers/:id" element={<CrmRoute><CrmCustomerDetail /></CrmRoute>} />
          <Route path="/pro/opportunities/:id" element={<CrmRoute><CrmOpportunityDetail /></CrmRoute>} />
          <Route path="/pro/settings" element={<CrmRoute><CrmSettings /></CrmRoute>} />

          {/* Nivoon Consulting (Nivoon-Consulting.md) */}
          <Route path="/consulting" element={<ConsultingRoute><ConsultingHome /></ConsultingRoute>} />
          <Route path="/consulting/clients" element={<ConsultingRoute><ConsultingClients /></ConsultingRoute>} />
          <Route path="/consulting/clients/:id" element={<ConsultingRoute><ConsultingClientDetail /></ConsultingRoute>} />
          <Route path="/consulting/rfps" element={<ConsultingRoute><ConsultingRfps /></ConsultingRoute>} />

          <Route path="/skills" element={<ProtectedRoute><MySkills /></ProtectedRoute>} />
          <Route path="/skills/matrix" element={<ProtectedRoute><SkillsMatrix /></ProtectedRoute>} />
          <Route path="/skills/team" element={<ProtectedRoute><TeamSkills /></ProtectedRoute>} />
          <Route path="/skills/bank" element={<ProtectedRoute><SkillBank /></ProtectedRoute>} />
          <Route path="/certifications" element={<ProtectedRoute><MyCertifications /></ProtectedRoute>} />
          <Route path="/certifications/dashboard" element={<ProtectedRoute><CertificationsDashboard /></ProtectedRoute>} />
          <Route path="/time-billing" element={<ProtectedRoute><MyTime /></ProtectedRoute>} />
          <Route path="/time-billing/reports" element={<ProtectedRoute><BillingReports /></ProtectedRoute>} />
          <Route path="/time-billing/rates" element={<CompanyAdminRoute><BillingRates /></CompanyAdminRoute>} />
          <Route path="/rate-card" element={<ProtectedRoute><StandardRateCard /></ProtectedRoute>} />
          <Route path="/time-billing/approvals" element={<CompanyAdminRoute><TimesheetApprovals /></CompanyAdminRoute>} />
          <Route path="/time-billing/invoices" element={<CompanyAdminRoute><Invoices /></CompanyAdminRoute>} />
          <Route path="/time-billing/invoices/:id" element={<CompanyAdminRoute><InvoiceDetail /></CompanyAdminRoute>} />
          <Route path="/resourcing" element={<ProtectedRoute><Resourcing /></ProtectedRoute>} />

          <Route
            path="/setup/company"
            element={
              <ProtectedRoute>
                <CompanySetupWizard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            }
          />

          <Route
            path="/teams"
            element={
              <AdminRoute>
                <TeamManagement />
              </AdminRoute>
            }
          />

          <Route
            path="/tech-stack"
            element={
              <AdminRoute>
                <TechStackManagement />
              </AdminRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <Projects />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/new"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <NewProjectWizard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:id/metrics"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectMetrics />
              </ProtectedRoute>
            }
          />

          <Route
            path="/meeting-notes"
            element={
              <ProtectedRoute>
                <MeetingNotesHub />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:id/meeting-notes"
            element={
              <ProtectedRoute>
                <MeetingNotes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/jira"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <JiraIntegration />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/gantt"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <Timeline2 />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/whiteboard"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectWhiteboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/registers"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectRegisters />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/documents"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/field-logs"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectFieldLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/progress-billing"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectProgressBilling />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/ai-insights"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <AIInsightsDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/approvals"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectApprovals />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/requirements"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <Requirements />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/ai-requirements"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <AIGeneratedRequirements />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/requirements/metrics"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <RequirementMetrics />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user-guide"
            element={
              <ProtectedRoute>
                <UserGuide />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/dashboard-v2"
            element={
              <PlatformAdminRoute>
                <PlatformAdminDashboardV2 />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/system-health"
            element={
              <AdminRoute>
                <SystemHealth />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/ai-config"
            element={
              <PlatformAdminRoute>
                <AIConfigurationPage />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/finances"
            element={
              <PlatformAdminRoute>
                <FinancesDashboard />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/pricing-plans"
            element={
              <PlatformAdminRoute>
                <PricingPlanManagement />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/company-subscriptions"
            element={
              <PlatformAdminRoute>
                <CompanySubscriptionManagement />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/stripe-payments"
            element={
              <PlatformAdminRoute>
                <StripePayments />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/jira-webhooks"
            element={
              <PlatformAdminRoute>
                <JiraWebhookLogs />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/blog"
            element={
              <PlatformAdminRoute>
                <BlogManagement />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/user-location-map"
            element={
              <PlatformAdminRoute>
                <UserLocationMap />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/ai-usage"
            element={
              <ProtectedRoute requiredRoles={['company_admin', 'platform_admin']}>
                <AIUsage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute>
                <CompanyManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/companies/:id"
            element={
              <ProtectedRoute>
                <CompanyDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/companies/:id/edit"
            element={
              <ProtectedRoute>
                <CompanyDetail />
              </ProtectedRoute>
            }
          />

          {/* Task Board - Backlog is default view */}
          <Route
            path="/taskboard"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <TaskBoardBacklog />
              </ProtectedRoute>
            }
          />

          <Route
            path="/taskboard/board"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <TaskBoard />
              </ProtectedRoute>
            }
          />

          {/* Legacy route redirect */}
          <Route
            path="/taskboard/backlog"
            element={<Navigate to="/taskboard" replace />}
          />

          <Route
            path="/taskboard/sprint-archive"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <SprintArchive />
              </ProtectedRoute>
            }
          />

          <Route
            path="/taskboard/requirement/:id"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <TaskBoardRequirementDetail />
              </ProtectedRoute>
            }
          />

          {/* Test Center */}
          <Route
            path="/test-center"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <TestCenter />
              </ProtectedRoute>
            }
          />

          <Route
            path="/test-center/runs/:id"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <TestRunDetail />
              </ProtectedRoute>
            }
          />

          {/* Wiki */}
          <Route
            path="/wiki"
            element={
              <ProtectedRoute>
                <Wiki />
              </ProtectedRoute>
            }
          />

          <Route
            path="/wiki/:slug"
            element={
              <ProtectedRoute>
                <Wiki />
              </ProtectedRoute>
            }
          />

          <Route
            path="/wiki/:slug/edit"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <Wiki />
              </ProtectedRoute>
            }
          />

          {/* Reports */}
          <Route
            path="/reports/sprint"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <SprintReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/project-archive"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <ProjectArchive />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/velocity"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <VelocityReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/leadership"
            element={
              <ProtectedRoute requiredRoles={['platform_admin', 'company_admin', 'project_manager', 'executive']}>
                <LeadershipReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/program-gantt"
            element={
              <ProtectedRoute requiredRoles={['platform_admin', 'company_admin', 'project_manager', 'executive']}>
                <ProgramGantt />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/time-tracking"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <TimeTrackingReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/requirement-aging"
            element={
              <ProtectedRoute requiredRoles={nonStakeholderRoles}>
                <RequirementAgingReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/release-notes"
            element={
              <ProtectedRoute requiredRoles={['platform_admin', 'company_admin', 'project_manager', 'project_manager_lite']}>
                <ReleaseNotes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/retrospectives"
            element={
              <ProtectedRoute requiredRoles={['platform_admin', 'company_admin', 'project_manager', 'project_manager_lite']}>
                <Retrospectives />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/dashboard-v2"
            element={
              <CompanyAdminRoute>
                <CompanyAdminDashboardV2 />
              </CompanyAdminRoute>
            }
          />

          <Route
            path="/company/strategic-projects"
            element={
              <ProtectedRoute requiredRoles={['platform_admin', 'company_admin', 'project_manager']}>
                <StrategicProjects />
              </ProtectedRoute>
            }
          />

          <Route
            path="/pm/dashboard-v2"
            element={
              <ProtectedRoute requiredRoles={['platform_admin', 'company_admin', 'project_manager', 'project_manager_lite']}>
                <PMDashboardV2 />
              </ProtectedRoute>
            }
          />

          {/* Customers */}
          <Route
            path="/customers"
            element={
              <CompanyAdminRoute>
                <Customers />
              </CompanyAdminRoute>
            }
          />
          <Route
            path="/customers/:customerId"
            element={
              <ProtectedRoute>
                <CustomerDetail />
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </Suspense>
          </BreadcrumbProvider>
          </CustomerProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
